import { useYupValidationResolver } from '@dreamstack/feature-components/src/hooks/useYupValidationResolver'
import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import {
  BodyText,
  HeadingH2,
  HeadingH4,
  Text_16_24,
} from '@dreamstack/feature-components/src/layout/Texts'
import { useFormatToCurrencyCutoff } from '@dreamstack/i18n'
import { Checkmark } from '@dreamstack/icons/src/icon-components/Checkmark'
import { Clear } from '@dreamstack/icons/src/icon-components/Clear'
import { SimpleInput } from '@dreamstack/simple-components/src/lib/SimpleInput'
import { SimpleRadio } from '@dreamstack/simple-components/src/lib/SimpleRadio'
import { cn } from '@dreamstack/util/src/cn'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { LtvInfoIcon } from './LtvInfoIcon'

const MeterDivider: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { transparent?: boolean }
> = ({ transparent, className, ...rest }) => (
  <div
    className={cn(
      'h-full w-[2px] relative',
      transparent ? 'bg-transparent' : 'bg-accentroAquaVeryLight-full',
      className
    )}
    {...rest}
  />
)

const PercentageLabelWrapper: React.FC<
  React.HTMLAttributes<HTMLParagraphElement> & { firstOrLast?: boolean }
> = ({ firstOrLast, className, ...rest }) => (
  <p
    className={cn(
      'pt-px-16 text-px-13 leading-px-18 -translate-x-1/2 translate-y-1/2 absolute -bottom-full',
      firstOrLast ? 'block' : 'hidden md:block',
      className
    )}
    {...rest}
  />
)

const BubbleWrapper: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { percentage?: number }
> = ({ percentage = 0, style, className, ...rest }) => (
  <div
    style={{ marginLeft: `${percentage}%`, ...style }}
    className={cn(className)}
    {...rest}
  />
)
const Bubble: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'mt-px-24 bg-accentroGray-full text-white relative rounded-full flex flex-col justify-center items-center h-px-80 w-px-80 -translate-x-1/2 p-[10px]',
      'md:h-px-196 md:w-px-196',
      className
    )}
    {...rest}
  />
)
const BubbleTriangle: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'absolute -bottom-px-4 rotate-45 left-1/2 -translate-x-1/2 bg-accentroGray-full h-px-12 w-px-12',
      'md:h-px-48 md:w-px-48',
      className
    )}
    {...rest}
  />
)
const BubbleSmallText: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  ...rest
}) => (
  <p
    className={cn(
      'text-center font-semibold text-[8px] leading-[12px]',
      'md:text-px-13 md:leading-px-18',
      className
    )}
    {...rest}
  />
)
const BubbleNumber: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  ...rest
}) => (
  <p
    className={cn(
      'text-center font-semibold text-px-28 leading-px-36',
      'md:text-px-72 md:leading-px-80',
      className
    )}
    {...rest}
  />
)
const BubbleSymbol: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  ...rest
}) => (
  <span
    className={cn(
      'font-semibold text-[11px] leading-[16.5px]',
      'md:text-px-28 md:leading-px-42',
      className
    )}
    {...rest}
  />
)
const Meter: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { internationalBuyer?: boolean }
> = ({ internationalBuyer, className, style, ...rest }) => (
  <div
    className={cn(
      'h-px-16 flex justify-between w-full mt-px-20 md:mt-px-32',
      className
    )}
    style={{
      background: internationalBuyer
        ? 'linear-gradient(to right, #73c49f 67%, #e94e25 72%, #e94e25)'
        : 'linear-gradient(to right, #73c49f 90%, #e94e25 95%, #e94e25)',
      ...style,
    }}
    {...rest}
  />
)

const CheckmarkCircle: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { ltvIsAccepted?: boolean }
> = ({ ltvIsAccepted, className, ...rest }) => (
  <div
    className={cn(
      'border-[3px] flex justify-center items-center rounded-full h-px-48 w-px-48 flex-shrink-0 mr-px-20',
      'md:h-px-80 md:w-px-80 md:mr-px-24',
      ltvIsAccepted
        ? 'border-accentroLightGreen-full'
        : 'border-accentroError-full',
      className
    )}
    {...rest}
  />
)

const schema = {
  propertyPrice: yup.number(),
  investment: yup.number(),
}

const calcualteLtv = (
  banksPropertyValuation: number,
  mortgageAmount: number
): number => {
  const ltv = Math.round((mortgageAmount / banksPropertyValuation) * 100)
  const boundariedLtv = ltv < 0 ? 0 : ltv > 100 ? 100 : ltv
  return boundariedLtv
}

export const LtvCalculator: React.FC = () => {
  const yupSchemaResolver = useYupValidationResolver(yup.object(schema))
  const methods = useForm<{
    buyer: string
    propertyPrice: number
    investment: number
  }>({
    resolver: yupSchemaResolver,
  })
  const { control, register, watch } = methods
  const { buyer, propertyPrice, investment } = watch()

  const [ltv, setLtv] = useState(67)
  const [banksPropertyValuation, setBanksPropertyValuation] = useState(0)
  const [mortgageAmount, setMortgageAmount] = useState(0)

  const format = useFormatToCurrencyCutoff()

  useEffect(() => {
    const banksPropertyValuation = propertyPrice * 0.9
    const mortgageAmount = propertyPrice - investment
    const ltv = calcualteLtv(banksPropertyValuation, mortgageAmount)
    setBanksPropertyValuation(banksPropertyValuation)
    setMortgageAmount(mortgageAmount > 0 ? mortgageAmount : 0)
    setLtv(ltv)
  }, [buyer, propertyPrice, investment])

  const meterDivider = useMemo(() => {
    return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((e, i) => {
      const percentageLabel = `${e * 10}%`
      const firstOrLast = [0, 10].includes(e)
      return (
        <MeterDivider transparent={firstOrLast} key={i}>
          <PercentageLabelWrapper firstOrLast={firstOrLast}>
            {percentageLabel}
          </PercentageLabelWrapper>
        </MeterDivider>
      )
    })
  }, [])

  const local = buyer === 'local'
  const ltvCliff = local ? 90 : 67
  const ltvIsAccepted = ltv <= ltvCliff
  const aboveBelow = ltvIsAccepted ? 'below' : 'above'
  const increaseDecrease = ltvIsAccepted ? 'increases' : 'decreases'
  const ltvInfoText = `Your LTV is ${aboveBelow} the LTV Cliff of ${ltvCliff}%, which ${increaseDecrease} the likelihood of getting approved by a German bank.`

  const { ref, ...registerBuyer } = register('buyer')
  return (
    <GridWithGaps>
      <HeadingH2 className="col-span-full">
        Calculate your individual LTV
      </HeadingH2>
      <BodyText className="col-span-full mt-px-24">
        When purchasing a property with a mortgage, you will often hear the term
        LTV, meaning Loan to Value. This is the percentage of the property price
        that will be the mortgage, which is calculated by how much money you can
        put into the investment. You can work out your personal LTV using the
        tool below:
      </BodyText>
      <FormProvider {...methods}>
        <form className="col-span-full">
          <div className="mt-px-32 md:mt-px-48">
            <SimpleRadio
              ref={ref}
              radioGroupProps={{
                ...registerBuyer,
                defaultValue: 'international',
                row: true,
              }}
              options={[
                { label: 'International Buyer', value: 'international' },
                { label: 'Local Buyer', value: 'local' },
              ]}
            />
          </div>
          <div className="mt-px-40">
            <div className="flex flex-col md:flex-row md:gap-x-px-48 ml:gap-x-px-32">
              <div className="space-y-px-16 md:space-y-px-24">
                <Controller
                  defaultValue={400_000}
                  name="propertyPrice"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={'Property Price'}
                      InputProps={{
                        endAdornment: 'EUR',
                        inputProps: { inputMode: 'numeric' },
                      }}
                    />
                  )}
                />
                <Controller
                  defaultValue={160_000}
                  name="investment"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={'How much can you invest?'}
                      InputProps={{
                        endAdornment: 'EUR',
                        inputProps: { inputMode: 'numeric' },
                      }}
                    />
                  )}
                />
              </div>
              <div className="space-y-px-16 mt-px-24 md:space-y-px-24 md:mt-0">
                <div>
                  <div className="flex flex-row justify-start items-center space-x-px-4 -mt-px-8">
                    <Text_16_24>Bank’s property valuation</Text_16_24>
                    <LtvInfoIcon />
                  </div>
                  <p className="-mt-px-12 text-px-16 leading-px-24 md:text-px-24 md:leading-px-36 font-semibold">
                    {format(banksPropertyValuation)} EUR
                  </p>
                </div>
                <div>
                  <p className="text-px-16 leading-px-24 md:text-px-20 md:leading-px-30">
                    Mortgage amount
                  </p>
                  <p className="text-px-24 leading-px-36 md:text-px-40 md:leading-px-50 font-semibold">
                    {format(mortgageAmount)}{' '}
                    <span className="text-px-16 leading-px-24 md:text-px-28 md:leading-px-42">
                      EUR
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <div className="col-span-full">
        <BubbleWrapper percentage={ltv}>
          <Bubble>
            <BubbleSmallText>Your individual LTV</BubbleSmallText>
            <BubbleNumber>
              {ltv}
              <BubbleSymbol>%</BubbleSymbol>
            </BubbleNumber>
            <BubbleTriangle />
          </Bubble>
        </BubbleWrapper>
        <Meter internationalBuyer={buyer === 'international'}>
          {meterDivider}
        </Meter>
      </div>
      <div className="col-span-full flex items-start mt-px-64 md:mt-px-80">
        <CheckmarkCircle ltvIsAccepted={ltvIsAccepted}>
          {ltvIsAccepted ? (
            <Checkmark className="text-accentroLightGreen-full md:h-px-40 md:w-px-40" />
          ) : (
            <Clear className="text-accentroError-full md:h-px-40 md:w-px-40" />
          )}
        </CheckmarkCircle>
        <div>
          <HeadingH4 className="font-semibold">{ltvInfoText} </HeadingH4>
          <p className="text-px-13 leading-px-18 text-accentroGray-500 mt-px-32">
            Loan-to-value = (loan amount after deduction of equity x 100) /
            mortgage lending value.
          </p>
        </div>
      </div>
    </GridWithGaps>
  )
}
